import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { X, Calendar, Clock, CheckCircle, Circle, XCircle, Award, ChevronDown, BookOpen, MoreVertical, Ban, Repeat, Pause, Dumbbell, Timer, Play } from 'lucide-react';
import { WorkoutSession, Exercise } from '../types/dataModel';
import { WorkoutService } from '../lib/workoutService';
import { ExerciseService } from '../lib/exerciseService';
import { getYouTubeThumbnail, isYouTubeUrl, getYouTubeEmbedUrl } from '../utils/youtubeUtils';
import { useAuth } from '../contexts/AuthContext';
import CacheService from '../lib/cacheService';
import { useCachedData } from '../hooks/useCachedData';
import { memberDashboardCacheKey } from '../lib/memberDashboardCache';
import { getSessionWorkoutBannerUrl } from '../lib/workoutBannerImage';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { CompleteWorkoutSlider } from '../components/CompleteWorkoutSlider';
import { motion, AnimatePresence } from 'framer-motion';
import { useDrag } from '@use-gesture/react';
import './WorkoutDetailsTheme.css';

/* ── Exercise-run grouping helpers (mirrors TrainerWorkoutDetailModal logic) ── */
function exGroupKey(s: any): string {
  if (s.exerciseGroupId != null && String(s.exerciseGroupId).trim() !== '') return `group:${s.exerciseGroupId}`;
  if (s.exerciseId && String(s.exerciseId).trim() !== '') return String(s.exerciseId);
  return '__no_exercise__';
}
interface ExRunView { gk: string; sets: any[]; }
function computeRunsView(sets: any[]): ExRunView[] {
  if (!sets?.length) return [];
  const runs: ExRunView[] = [];
  let curKey = exGroupKey(sets[0]);
  let cur: any[] = [sets[0]];
  for (let i = 1; i < sets.length; i++) {
    const k = exGroupKey(sets[i]);
    if (k !== curKey) { runs.push({ gk: curKey, sets: cur }); curKey = k; cur = [sets[i]]; }
    else cur.push(sets[i]);
  }
  runs.push({ gk: curKey, sets: cur });
  return runs;
}
function uniformMeasureView(sets: any[]): boolean {
  if (sets.length <= 1) return true;
  const isDur = sets[0]?.durationSeconds != null && sets[0].durationSeconds > 0;
  if (isDur) { const v = sets[0].durationSeconds; return sets.every((s: any) => s.durationSeconds === v); }
  const rt0 = sets[0]?.repText ?? (sets[0]?.rep != null ? String(sets[0].rep) : '');
  return sets.every((s: any) => (s.repText ?? (s.rep != null ? String(s.rep) : '')) === rt0);
}
function uniformRestView(sets: any[]): boolean {
  if (sets.length <= 1) return true;
  const v = sets[0]?.restSeconds ?? 0;
  return sets.every((s: any) => (s.restSeconds ?? 0) === v);
}

/** v2: entries invalidated so localStorage isn’t stuck on pre-hydration session shapes */
const workoutDetailsCacheKey = (paramKey: string, memberKey: string) =>
  `workout_details_v2_${paramKey}_${memberKey}`;

/** Status bar / theme-color are driven here (hero vs sticky scroll), not usePageStatusBar. */
export const WorkoutDetailsPage = () => {
  const headerMinimizedRef = useRef(false);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add('workout-details-bleed');
    root.classList.add('workout-details-bar-hero-top');
    const meta = document.querySelector('meta[name="theme-color"]');
    const apple = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    meta?.setAttribute('content', '#000000');
    apple?.setAttribute('content', 'black-translucent');
    return () => {
      root.classList.remove('workout-details-bleed');
      root.classList.remove('workout-details-bar-hero-top');
      root.classList.remove('workout-details-bar-sticky');
    };
  }, []);

  const { param } = useParams<{ param: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const fromReschedule = (location.state as { fromReschedule?: boolean } | null)?.fromReschedule === true;
  const [sessions, setSessions] = useState<(WorkoutSession & { workout?: any })[]>([]);
  const [openActionsMenuId, setOpenActionsMenuId] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [sliderCompleting, setSliderCompleting] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [newScheduledDate, setNewScheduledDate] = useState<string>('');
  const [showRescheduleConfirmation, setShowRescheduleConfirmation] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [headerMinimized, setHeaderMinimized] = useState(false);
  const [stickyBarMounted, setStickyBarMounted] = useState(false);
  const [sheetDragY, setSheetDragY] = useState(0);
  const [exerciseSheetDragY, setExerciseSheetDragY] = useState(0);
  /** YouTube: show poster + tap to load iframe (keeps playback in the sheet on mobile). */
  const [exerciseVideoEmbedActive, setExerciseVideoEmbedActive] = useState(false);

  // Hard-drop legacy date links (Notifications + reschedule are being migrated to sessionId-only routes).
  useEffect(() => {
    if (!param) return;
    if (/^\d{4}-\d{2}-\d{2}$/.test(param)) {
      navigate('/member-dashboard', { replace: true });
    }
  }, [param, navigate]);

  const SHEET_DISMISS_THRESHOLD = 100;

  const sheetDragBind = useDrag(
    (state) => {
      const dy = Math.max(0, state.movement[1]);
      if (state.active) {
        setSheetDragY(dy);
      } else {
        if (dy > SHEET_DISMISS_THRESHOLD) {
          setOpenActionsMenuId(null);
        }
        setSheetDragY(0);
      }
    },
    { axis: 'y', pointer: { touch: true } }
  );

  const exerciseSheetDragBind = useDrag(
    (state) => {
      const dy = Math.max(0, state.movement[1]);
      if (state.active) {
        setExerciseSheetDragY(dy);
      } else {
        if (dy > SHEET_DISMISS_THRESHOLD) {
          setSelectedExercise(null);
        }
        setExerciseSheetDragY(0);
      }
    },
    { axis: 'y', pointer: { touch: true } }
  );

  // Sticky bar + status bar / document shell: sync with scroll (hero top → black; mid → light+translucent; sticky → light+translucent)
  useEffect(() => {
    const SCROLL_MINIMIZE = 400; /* ~banner-style hero height; show sticky bar when scrolled past this */
    const SCROLL_EXPAND = 340;   /* hide sticky bar when scrolled back above this */

    const getScrollY = () => {
      if (typeof window === 'undefined') return 0;
      /*
       * Use window scroll only: `w || doc` style reads are wrong when scrollY is 0 (falsy),
       * and iOS can leave documentElement.scrollTop > 0 while the page is visually at the top.
       */
      return Math.max(0, window.scrollY ?? window.pageYOffset ?? 0);
    };

    const syncWorkoutChrome = (y: number, stickyVisible: boolean) => {
      const root = document.documentElement;
      const meta = document.querySelector('meta[name="theme-color"]');
      const apple = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (!meta || !apple) return;
      const dark = root.classList.contains('dark');

      /*
       * Never use apple meta "default" here: iOS standalone often fails to switch back to
       * black-translucent after default, so the status bar stays white when returning to top.
       * Keep black-translucent always; match sticky/mid/hero via theme-color + html classes only.
       */
      apple.setAttribute('content', 'black-translucent');

      /*
       * Order matters: sticky wins when the compact bar is shown (hysteresis can keep it true
       * between SCROLL_EXPAND–SCROLL_MINIMIZE). Otherwise, any scroll position still “in” the hero
       * (before SCROLL_MINIMIZE) must use black + hero-top — not a tiny y<72 band, which left the
       * bar white for most of the hero after scrolling back from mid-page.
       */
      if (stickyVisible) {
        root.classList.remove('workout-details-bar-hero-top');
        root.classList.add('workout-details-bar-sticky');
        meta.setAttribute('content', dark ? 'var(--ink)' : '#ffffff');
      } else if (y < SCROLL_MINIMIZE) {
        root.classList.add('workout-details-bar-hero-top');
        root.classList.remove('workout-details-bar-sticky');
        meta.setAttribute('content', '#000000');
      } else {
        root.classList.remove('workout-details-bar-hero-top');
        root.classList.remove('workout-details-bar-sticky');
        meta.setAttribute('content', dark ? 'var(--ink)' : '#ffffff');
      }
    };

    const onScroll = () => {
      const y = getScrollY();
      let min = headerMinimizedRef.current;
      if (y > SCROLL_MINIMIZE) min = true;
      else if (y < SCROLL_EXPAND) min = false;
      headerMinimizedRef.current = min;
      setHeaderMinimized(min);
      setStickyBarMounted((mounted) => mounted || y > SCROLL_MINIMIZE);
      syncWorkoutChrome(y, min);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    /* Final sync after momentum scroll settles (iOS); helps when scrollY snaps to 0 without extra scroll events */
    window.addEventListener('scrollend', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('scrollend', onScroll);
    };
  }, []);

  // Fetch sessions: by date (legacy / notifications) or by session ID (dashboard banner)
  const fetchSessions = useCallback(async () => {
    if (!param || !user) return [];
    // If the param is a date key, we’re redirecting out; avoid fetching.
    if (/^\d{4}-\d{2}-\d{2}$/.test(param)) return [];

    const session = await WorkoutService.getWorkoutSessionById(param);
    if (!session) return [];
    return [await WorkoutService.hydrateSessionWorkoutIfNeeded(session)];
  }, [param, user]);

  // Get memberId for cache key consistency
  const memberId = profile?.id || user?.id || '';

  const assignmentId = sessions[0]?.usersWorkoutplanId ?? null;
  const expectsPlanName = Boolean(assignmentId);

  /** Same payload MemberDashboard writes — available on first paint when user came from dashboard */
  const planNameFromDashboardCache = useMemo(() => {
    if (!memberId || !assignmentId) return null;
    const dash = CacheService.get<{
      plans?: Array<{ id: string; workoutPlan?: { name?: string } }>;
    }>(memberDashboardCacheKey(memberId));
    const row = dash?.plans?.find((p) => p.id === assignmentId);
    const n = row?.workoutPlan?.name;
    return typeof n === 'string' && n.trim() !== '' ? n.trim() : null;
  }, [memberId, assignmentId]);

  /** undefined = request in flight; null = loaded with no name */
  const [fetchedPlanName, setFetchedPlanName] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!memberId || !assignmentId) {
      setFetchedPlanName(undefined);
      return;
    }
    setFetchedPlanName(undefined);
    let cancelled = false;
    WorkoutService.getMemberWorkoutPlansWithDetails(memberId)
      .then((plans) => {
        if (cancelled) return;
        const row = plans.find((p: any) => p.id === assignmentId);
        const n = row?.workoutPlan?.name;
        setFetchedPlanName(typeof n === 'string' && n.trim() !== '' ? n.trim() : null);
      })
      .catch(() => {
        if (!cancelled) setFetchedPlanName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, assignmentId]);

  const resolvedPlanLabel =
    fetchedPlanName !== undefined ? fetchedPlanName : planNameFromDashboardCache;

  const showPlanEyebrowRow =
    expectsPlanName && (resolvedPlanLabel !== null || fetchedPlanName === undefined);

  const planEyebrowPending = showPlanEyebrowRow && !resolvedPlanLabel;

  // Hide body scrollbar when a bottom sheet is open to prevent bleed-through
  useEffect(() => {
    if (openActionsMenuId || selectedExercise) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [openActionsMenuId, selectedExercise]);

  // Reset YouTube poster when switching exercises or closing the sheet
  useEffect(() => {
    setExerciseVideoEmbedActive(false);
  }, [selectedExercise?.id]);

  // Unique equipment from first session's workout (hook must run unconditionally)
  const uniqueEquipment = useMemo(() => {
    const workout = sessions[0]?.workout;
    if (!workout?.workoutBlocks) return [];
    const set = new Set<string>();
    for (const block of workout.workoutBlocks) {
      for (const es of block.exerciseSets || []) {
        const eq = (es.exercise?.equipment ?? '').trim();
        if (eq) set.add(eq);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [sessions]);

  // Use cached data hook
  const {
    data: sessionsData,
    loading,
    error: dataError,
    refetch,
  } = useCachedData({
    cacheKey: workoutDetailsCacheKey(param ?? '', memberId),
    fetchFn: fetchSessions,
    enabled: !!param && !!user,
  });

  // Update sessions state when cached data is available
  useEffect(() => {
    if (sessionsData) {
      setSessions(sessionsData);
    }
  }, [sessionsData]);

  // Fetch dashboard data (6 months back, 6 ahead) and set cache so closing the page shows fresh calendar
  const refreshDashboardCache = useCallback(async (memberId: string) => {
    const DASHBOARD_TTL = 1 * 60 * 1000;
    const plans = await WorkoutService.getMemberWorkoutPlansWithDetails(memberId);
    const active = plans.find((p: any) => p.status === 'active') || plans[0];
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth() - 6, 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 6 + 1, 0);
    const loadedRange = { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    const [sessions, _version] = await Promise.all([
      WorkoutService.getMemberWorkoutSessions(memberId, {
        fromDate: loadedRange.start,
        toDate: loadedRange.end,
      }),
      WorkoutService.getMemberDashboardVersion(memberId),
    ]);
    CacheService.set(
      memberDashboardCacheKey(memberId),
      { plans, active, sessions, loadedRange, _version },
      { ttl: DASHBOARD_TTL }
    );
  }, []);

  // Helper function to get video thumbnail (YouTube or regular image)
  const getVideoThumbnail = (videoUrl?: string): string | null => {
    return getYouTubeThumbnail(videoUrl);
  };

  // Helper function to format block type for display
  const formatBlockType = (type: string): string => {
    if (type === 'Default') return '';
    return type.replace(/_/g, ' ');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCompletionDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Check if the date is today
  const isToday = (dateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sessionDate = new Date(dateString);
    sessionDate.setHours(0, 0, 0, 0);
    return today.getTime() === sessionDate.getTime();
  };

  const statusOptions: WorkoutSession['status'][] = ['scheduled', 'in_progress', 'completed', 'skipped'];

  const handleStatusChange = async (sessionId: string, newStatus: WorkoutSession['status']) => {
    setUpdatingStatus(sessionId);
    setOpenActionsMenuId(null);

    try {
      // Prepare additional data based on status
      const additionalData: any = {};
      const currentSession = sessions.find(s => s.id === sessionId);
      const wasCompleted = currentSession?.status === 'completed';
      
      if (newStatus === 'in_progress' && !currentSession?.startedAt) {
        additionalData.startedAt = new Date().toISOString();
      }
      
      if (newStatus === 'completed' && !currentSession?.completedAt) {
        additionalData.completedAt = new Date().toISOString();
        const startedAt = currentSession?.startedAt;
        if (startedAt) {
          const durationMinutes = Math.floor((Date.now() - new Date(startedAt).getTime()) / 60000);
          additionalData.actualDurationMinutes = durationMinutes;
        }
      }
      
      // If changing FROM completed to something else, clear completedAt and actualDurationMinutes
      if (wasCompleted && newStatus !== 'completed') {
        additionalData.completedAt = null;
        additionalData.actualDurationMinutes = null;
      }

      await WorkoutService.updateWorkoutSessionStatus(sessionId, newStatus, additionalData);

      // Update local state immediately for instant UI feedback
      const updatedSessions = sessions.map(session =>
        session.id === sessionId
          ? { 
              ...session, 
              status: newStatus, 
              ...additionalData,
              // Explicitly clear completedAt and actualDurationMinutes if they're null
              completedAt: additionalData.completedAt === null ? undefined : (additionalData.completedAt || session.completedAt),
              actualDurationMinutes: additionalData.actualDurationMinutes === null ? undefined : (additionalData.actualDurationMinutes !== undefined ? additionalData.actualDurationMinutes : session.actualDurationMinutes)
            }
          : session
      );
      
      setSessions(updatedSessions);

      // Update caches with new data immediately so closing the page shows fresh data without loading
      if (user && memberId) {
        const dashboardCacheKey = memberDashboardCacheKey(memberId);
        const currentPageCacheKey = workoutDetailsCacheKey(param ?? '', memberId);
        const DASHBOARD_TTL = 1 * 60 * 1000;

        // Merge updated sessions into dashboard cache so calendar is fresh when user closes
        type DashboardCache = {
          plans: any[];
          active: any;
          sessions: (WorkoutSession & { workout?: any })[];
          loadedRange?: { start: string; end: string };
        };
        const cachedDashboard = CacheService.get<DashboardCache>(dashboardCacheKey);
        if (cachedDashboard?.sessions) {
          const mergedSessions = cachedDashboard.sessions.map((s) => {
            const updated = updatedSessions.find((u) => u.id === s.id);
            return updated ?? s;
          });
          const merged = {
            ...cachedDashboard,
            sessions: mergedSessions,
          };
          const _version = await WorkoutService.getMemberDashboardVersion(memberId);
          CacheService.set(
            dashboardCacheKey,
            { ...merged, _version },
            { ttl: DASHBOARD_TTL }
          );
        }

        // Update current page cache with the new data immediately
        CacheService.set(currentPageCacheKey, updatedSessions, { ttl: 10 * 60 * 1000 });
        
        console.log('✅ Status changed - updated dashboard and page cache:', { newStatus, sessionId });
      }
    } catch (error) {
      console.error('Error updating session status:', error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusColor = (status: string) => {
    return 'bg-theme-muted dark:bg-white/10 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/20';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-theme-bg-dark" />;
      case 'in_progress':
        return <Circle className="w-4 h-4" />;
      case 'skipped':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Circle className="w-4 h-4" />;
    }
  };

  const handleExerciseClick = (exercise: Exercise | { id: string; name?: string; muscleGroup?: string; description?: string; image?: string; video?: string }) => {
    if (!exercise) return;
    setOpenActionsMenuId(null);
    // Use the exercise from the workout context (includes snapshot data when trainer customized it)
    // so members see the trainer's version, not the base library exercise
    setSelectedExercise(exercise as Exercise);
  };

  if (loading) {
    return <LoadingSpinner fullScreen message={fromReschedule ? 'Updating the calendar' : undefined} />;
  }

  if (rescheduling) {
    return <LoadingSpinner fullScreen message="Updating the calendar" />;
  }

  if (!param || sessions.length === 0) {
    return (
      <div className="workout-details-page workout-details-page--empty min-h-screen overflow-x-hidden pb-bottom-nav">
        <div className="workout-details-header relative">
          <div className="px-4 pt-4 pb-2">
            <button
              onClick={() => navigate('/member-dashboard')}
              className="absolute right-4 p-2 rounded-lg transition-colors z-10 top-[calc(0.75rem+env(safe-area-inset-top))]"
              aria-label="Close"
            >
              <X className="w-6 h-6 workout-details-header-text" />
            </button>
          </div>
        </div>
        <div className="workout-details-content bg-white dark:bg-theme-bg-dark p-4 md:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-lg p-8 text-center">
              <p className="text-theme-text/80 dark:text-theme-off-white/80">No workouts found for this date.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get workout name and note from first session (assuming all sessions on same date have same workout)
  const workoutName = sessions.length > 0 ? (sessions[0].workout?.name || 'Workout') : 'Workout';
  const workoutNote = sessions.length > 0 ? sessions[0].workout?.note : null;
  const trainerNote = sessions.length > 0 ? sessions[0].notes : null;
  const difficulty = sessions.length > 0 ? sessions[0].workout?.difficulty : null;
  const estimatedDurationMinutes = sessions.length > 0 ? sessions[0].workout?.estimatedDurationMinutes : null;
  const displayDate = sessions[0]?.scheduledDate?.split('T')[0];
  const bannerImageUrl = getSessionWorkoutBannerUrl(sessions[0]);

  return (
    <div className="workout-details-page min-h-screen relative">
      {/* Full header: in flow, scrolls away. Close button at same position as sticky header (top-3 right-4). */}
      <header className="workout-details-header workout-details-header--hero">
        {/* Upper band: image + same top/bottom gradients as member dashboard workout banner */}
        <div className="workout-details-header__media">
          <div
            className="workout-details-header__bg"
            style={{
              backgroundImage: `url(${bannerImageUrl})`,
            }}
            aria-hidden
          />
          <div className="workout-details-header__gradient-top" aria-hidden />
          <div className="workout-details-header__gradient-bottom" aria-hidden />
          <button
            onClick={() => navigate('/member-dashboard')}
            className="absolute right-4 p-2 rounded-lg transition-colors flex-shrink-0 z-20 workout-details-header__close top-[calc(0.75rem+env(safe-area-inset-top))]"
            aria-label="Close"
          >
            <X className="w-6 h-6 workout-details-header-text" />
          </button>
        </div>
        {/* Solid black strip — matches dashboard banner bottom bar */}
        <div className="workout-details-header__bottom-bar" aria-hidden />
        {/* Title + meta overlay: pinned to header bottom like dashboard banner copy */}
        <div className="workout-details-header__bottom-content">
          {displayDate ? (
            <div className="workout-details-header__eyebrow">
              <span className="workout-details-header__eyebrow-date">
                {isToday(displayDate) ? "Today's workout" : formatDate(displayDate)}
              </span>
            </div>
          ) : null}
          <div className="workout-details-header__title-stack">
            {showPlanEyebrowRow ? (
              <span
                className={
                  'workout-details-header__eyebrow-plan' +
                  (planEyebrowPending ? ' workout-details-header__eyebrow-plan--pending' : '')
                }
              >
                {resolvedPlanLabel || '\u00a0'}
              </span>
            ) : null}
            <h1 className="workout-details-header__title workout-details-header-text font-heading font-bold line-clamp-2">
              {workoutName}
            </h1>
          </div>
          {(estimatedDurationMinutes != null || difficulty) ? (
            <div className="workout-details-header__meta-row">
              {estimatedDurationMinutes != null ? (
                <span className="workout-details-header__meta-item workout-details-header-muted">
                  <Clock className="workout-details-header__meta-icon" aria-hidden />
                  {estimatedDurationMinutes} min
                </span>
              ) : null}
              {estimatedDurationMinutes != null && difficulty ? (
                <span className="workout-details-header__meta-sep" aria-hidden>
                  ·
                </span>
              ) : null}
              {difficulty ? (
                <span className="workout-details-difficulty-badge inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize">
                  {difficulty}
                </span>
              ) : null}
            </div>
          ) : null}
          {trainerNote ? (
            <p className="workout-details-header__trainer-note workout-details-header-muted">
              <span className="workout-details-header__trainer-note-label">Trainer note</span>
              {trainerNote}
            </p>
          ) : null}
        </div>
      </header>

      {/* Sticky bar: mount after scroll check to avoid flash; fades in/out when scrolling */}
      {stickyBarMounted && (
      <div
        className={`workout-details-header workout-details-header--sticky workout-details-header--minimized workout-details-header--fixed ${headerMinimized ? 'workout-details-header--visible' : ''}`}
        aria-hidden={!headerMinimized}
      >
        <div className="workout-details-header-compact px-4 py-3 flex items-center justify-between">
          <h1 className="workout-details-header-compact-title text-base font-heading font-semibold workout-details-header-text line-clamp-1 truncate flex-1 min-w-0 mr-2">
            {workoutName}
          </h1>
          <button
            onClick={() => navigate('/member-dashboard')}
            className="p-2 rounded-lg transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-6 h-6 workout-details-header-text" />
          </button>
        </div>
      </div>
      )}

      {/* Content: white section (overflow-x-hidden here so sticky header is not inside overflow ancestor) */}
      <div
        className="workout-details-content workout-details-content--body bg-white dark:bg-theme-bg-dark px-4 overflow-x-hidden md:px-8"
        style={{ paddingBottom: 'calc(4.5rem + env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-4xl mx-auto workout-details-content__inner">
          {/* Description (workout note) - above equipment */}
          {workoutNote && (
            <section className="workout-details-section workout-details-section--description">
              <h2 className="workout-details-section-title">Description</h2>
              <p className="workout-details-section-body">{workoutNote}</p>
            </section>
          )}

          {/* Equipment section (above first exercise block) */}
          <section className={`workout-details-section workout-details-section--equipment ${!workoutNote ? 'workout-details-section--first' : ''}`}>
            <h2 className="workout-details-section-title">Equipment</h2>
            <div className="flex flex-wrap gap-2">
              {uniqueEquipment.length > 0 ? (
                uniqueEquipment.map((eq) => (
                  <span
                    key={eq}
                    className="workout-details-equipment-tag inline-block px-3 py-1.5 rounded-full text-sm font-medium bg-theme-muted dark:bg-white/10 text-theme-text dark:text-theme-off-white"
                  >
                    {eq}
                  </span>
                ))
              ) : (
                <span className="text-base text-theme-text/60 dark:text-theme-off-white/60">No equipment</span>
              )}
            </div>
          </section>

          {/* Workout Sessions */}
          <div className="workout-details-sessions space-y-8">
            {sessions.map((session) => (
              <div
                key={session.id}
                className="rounded-lg"
              >
                {/* Workout Header */}
                {session.workout?.description && (
                  <div className="mb-5">
                    <p className="text-base leading-relaxed text-theme-text/75 dark:text-theme-off-white/75">
                      {session.workout.description}
                    </p>
                  </div>
                )}

              {/* Workout Blocks: block title only, exercises below; separators between blocks */}
                  {session.workout?.workoutBlocks && session.workout.workoutBlocks.length > 0 ? (
                <div className="workout-details-block-list divide-y divide-theme-border dark:divide-white/10 [&>:first-child]:border-t-0 mt-2">
                  {session.workout.workoutBlocks.map((block: any, blockIndex: number) => (
                    <div key={blockIndex} className="workout-details-block bg-white dark:bg-theme-surface w-screen max-w-none ml-[calc(50%-50vw)] mr-[calc(50%-50vw)] px-4 py-5 md:px-8 md:py-6 space-y-4 [&:last-child]:pb-3">
                      {/* Block header: title + badges */}
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="workout-details-block-title font-semibold text-theme-text dark:text-theme-off-white text-lg sm:text-xl tracking-tight">
                          {block.name || `Block ${blockIndex + 1}`}
                        </h3>
                        {block.type && block.type !== 'Default' && (
<span className="px-2 py-1 rounded-full text-xs font-medium bg-theme-muted dark:bg-white/10 text-theme-text dark:text-theme-off-white uppercase">
                              {formatBlockType(block.type)}
                            </span>
                        )}
                        {block.type && block.type !== 'Default' && block.exerciseSets.length > 1 && block.sets && (
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary-400 text-gray-800 dark:text-gray-900">
                            {block.sets} {block.sets === 1 ? 'SET' : 'SETS'}
                          </span>
                        )}
                      </div>
                      {/* Hint for circuit/superset: do exercises in order, then repeat */}
                      {block.type && block.type !== 'Default' && block.exerciseSets.length > 1 && (
                        <p className="workout-details-block-hint flex items-start gap-2.5 text-sm leading-relaxed text-theme-text/72 dark:text-theme-off-white/72">
                          <Repeat className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden />
                          <span>Do each exercise in order, repeat{block.sets ? ` for ${block.sets} ${block.sets === 1 ? 'set' : 'sets'}` : ''}</span>
                        </p>
                      )}

                      {/* Exercise cards grouped by exercise run */}
                      {block.exerciseSets && block.exerciseSets.length > 0 && (
                        <div className="divide-y divide-theme-border dark:divide-white/10 [&>:first-child]:border-t-0">
                          {computeRunsView(block.exerciseSets).map((run) => {
                              const firstSet = run.sets[0];
                              const thumbnail = getVideoThumbnail(firstSet?.exercise?.video);
                              const singleSet = run.sets.length === 1;
                              const uMeasure = uniformMeasureView(run.sets);
                              const uRest = uniformRestView(run.sets);
                              const restVal = firstSet?.restSeconds ?? 0;
                              const isDur = firstSet?.durationSeconds != null && firstSet.durationSeconds > 0 && !(firstSet.repText || firstSet.rep);

                              return (
                                <div
                                  key={run.gk}
                                  onClick={() => {
                                    if (firstSet?.exercise?.id) {
                                      handleExerciseClick(firstSet.exercise);
                                    }
                                  }}
                                  className="workout-details-exercise-row bg-white dark:bg-theme-surface py-4 hover:bg-theme-muted dark:hover:bg-white/10 transition-all cursor-pointer"
                                >
                                <div className="flex items-start gap-3.5">
                                  {thumbnail ? (
                                    <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-theme-border dark:bg-white/10">
                                      <img src={thumbnail} alt={firstSet?.exercise?.name || 'Exercise'} className="w-full h-full object-cover" />
                                    </div>
                                  ) : firstSet?.exercise?.image ? (
                                    <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-theme-border dark:bg-white/10">
                                      <img src={firstSet.exercise.image} alt={firstSet?.exercise?.name || 'Exercise'} className="w-full h-full object-cover" />
                                    </div>
                                  ) : (
                                    <div className="workout-details-exercise-thumbnail-placeholder flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center">
                                      <Dumbbell className="w-6 h-6 text-black dark:text-white" strokeWidth={2} />
                                    </div>
                                  )}

                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-base font-semibold leading-snug text-theme-text dark:text-theme-off-white mb-1">
                                      {firstSet?.exercise?.name || 'Exercise'}
                                    </h4>
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      {firstSet?.exercise?.muscleGroup && (
                                        <span className="workout-details-muscle-tag">{firstSet.exercise.muscleGroup}</span>
                                      )}
                                      {firstSet?.exercise?.equipment && (
                                        <span className="workout-details-equipment-tag-inline">{firstSet.exercise.equipment}</span>
                                      )}
                                    </div>
                                  </div>

                                  <div className="workout-details-exercise-dose text-right ml-3 flex-shrink-0 tabular-nums">
                                    {(() => {
                                      const needsPerSetLines = !singleSet && (!uMeasure || !uRest);

                                      if (!needsPerSetLines) {
                                        const s = firstSet;
                                        const repDisp = (s.repText && String(s.repText).trim())
                                          ? `${String(s.repText).trim()} reps`
                                          : (s.rep && s.rep > 0) ? `${s.rep} ${s.rep === 1 ? 'rep' : 'reps'}` : null;
                                        const durDisp = s.durationSeconds ? `${s.durationSeconds}s` : null;
                                        return (
                                          <div className="text-sm font-semibold text-theme-text dark:text-theme-off-white">
                                            {isDur ? durDisp : repDisp}
                                          </div>
                                        );
                                      }
                                      return run.sets.map((set: any, si: number) => {
                                        const repDisp = (set.repText && String(set.repText).trim())
                                          ? `${String(set.repText).trim()} reps`
                                          : (set.rep && set.rep > 0) ? `${set.rep} ${set.rep === 1 ? 'rep' : 'reps'}` : null;
                                        const durDisp = set.durationSeconds ? `${set.durationSeconds}s` : null;
                                        const perSetRest = !uRest && set.restSeconds != null && set.restSeconds > 0
                                          ? `Rest ${set.restSeconds}s` : null;
                                        return (
                                          <div key={set.id || si} className="text-sm text-theme-text dark:text-theme-off-white">
                                            <span className="text-theme-text/50 text-xs">Set {si + 1}: </span>
                                            <span className="font-semibold">{isDur ? durDisp : repDisp}</span>
                                            {perSetRest && <span className="text-xs text-theme-text/50 ml-1">· {perSetRest}</span>}
                                          </div>
                                        );
                                      });
                                    })()}
                                  </div>
                                </div>
                                {uRest && restVal > 0 ? (
                                  <div
                                    className="workout-details-exercise-rest"
                                    aria-label={`Rest ${restVal} seconds before next exercise`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <span className="workout-details-exercise-rest-line" aria-hidden />
                                    <span className="workout-details-exercise-rest-core">
                                      <Timer className="workout-details-exercise-rest-icon" aria-hidden />
                                      <span className="workout-details-exercise-rest-text">
                                        Rest <span className="tabular-nums">{restVal}s</span>
                                      </span>
                                    </span>
                                    <span className="workout-details-exercise-rest-line" aria-hidden />
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Block note (no box) */}
                      {block.note && (
                        <p className="text-sm leading-relaxed text-theme-text/72 dark:text-theme-off-white/72 italic mt-1">
                          <span className="font-semibold not-italic">Note:</span> {block.note}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-theme-text/60 dark:text-theme-off-white/60 text-base leading-relaxed mt-4">No exercises found for this workout.</p>
              )}

              {/* Notes */}
              {session.notes && (
                <div className="mt-6 pt-5 border-t border-theme-border dark:border-white/10">
                  <p className="text-base leading-relaxed text-theme-text/75 dark:text-theme-off-white/75">
                    <span className="font-semibold text-theme-text dark:text-theme-off-white">Notes:</span> {session.notes}
                  </p>
                </div>
              )}
            </div>
          ))}
          </div>
        </div>
      </div>

      {/* Reschedule Modal */}
      {showRescheduleModal && sessions.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-end justify-center"
          style={{ 
            paddingLeft: 'max(1rem, env(safe-area-inset-left))',
            paddingRight: 'max(1rem, env(safe-area-inset-right))',
            paddingBottom: 'calc(5rem + env(safe-area-inset-bottom))'
          }}
          onClick={() => setShowRescheduleModal(false)}
        >
          <div 
            className="bg-theme-bg dark:bg-theme-bg-dark rounded-t-lg shadow-2xl border-t border-theme-border dark:border-white/30 w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ 
              width: '100%',
              maxWidth: '100%'
            }}
          >
            <div className="overflow-hidden" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem', paddingLeft: '1rem', paddingRight: '1rem' }}>
              <h3 className="text-lg font-semibold text-theme-text dark:text-theme-off-white mb-4">Reschedule Workout</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-theme-text/80 dark:text-theme-off-white/80 mb-2">
                  Select New Date
                </label>
                <div className="relative" style={{ width: '100%', minWidth: 0 }}>
                  <input
                    type="date"
                    value={newScheduledDate}
                    onChange={(e) => setNewScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-3 rounded-lg bg-theme-bg dark:bg-white/5 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm"
                    style={{ 
                      colorScheme: 'dark',
                      boxSizing: 'border-box',
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }}
                  />
                </div>
              </div>

              <div className="flex gap-3" style={{ minWidth: 0 }}>
                <button
                  onClick={() => {
                    setShowRescheduleModal(false);
                    setNewScheduledDate('');
                  }}
                  className="flex-1 px-4 py-3 rounded-lg bg-theme-muted dark:bg-white/5 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/10 hover:bg-theme-border dark:hover:bg-white/10 transition-colors font-medium min-w-0"
                  style={{ flex: '1 1 0', minWidth: 0 }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!newScheduledDate || newScheduledDate === sessions[0].scheduledDate.split('T')[0]) {
                      setShowRescheduleModal(false);
                      return;
                    }
                    setShowRescheduleModal(false);
                    setShowRescheduleConfirmation(true);
                  }}
                  disabled={!newScheduledDate || newScheduledDate === sessions[0].scheduledDate.split('T')[0]}
                  className="flex-1 px-4 py-3 rounded-lg bg-primary-400/20 text-theme-bg-dark dark:text-gray-900 border border-primary-400/50 hover:bg-primary-400/30 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed min-w-0"
                  style={{ flex: '1 1 0', minWidth: 0 }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Confirmation Modal */}
      {showRescheduleConfirmation && sessions.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-end justify-center p-4"
          style={{ paddingBottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
          onClick={() => {
            setShowRescheduleConfirmation(false);
            setNewScheduledDate('');
          }}
        >
          <div 
            className="bg-theme-bg dark:bg-theme-bg-dark rounded-t-lg shadow-2xl border-t border-theme-border dark:border-white/30 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-theme-text dark:text-theme-off-white mb-4">Reschedule Options</h3>
              
              <p className="text-theme-text/80 dark:text-theme-off-white/80 mb-6">
                Do you want to reschedule only this workout, or move all future workouts as well?
              </p>

              <div className="space-y-3">
                <button
                  onClick={async () => {
                    const memberId = profile?.id || user?.id;
                    if (!memberId) return;
                    setRescheduling(true);
                    try {
                      const sessionId = sessions[0].id;
                      
                      await WorkoutService.updateWorkoutSessionDate(sessionId, newScheduledDate);
                      // Refresh dashboard cache so closing the page shows fresh calendar without loading
                      await refreshDashboardCache(memberId);
                      
                      // Keep URL stable (session-id route). Update this page in-place + invalidate only this page's key.
                      CacheService.invalidate(workoutDetailsCacheKey(param ?? '', memberId));
                      
                      setShowRescheduleConfirmation(false);
                      setNewScheduledDate('');
                      await refetch();
                    } catch (error) {
                      console.error('Error rescheduling workout:', error);
                      alert('Failed to reschedule workout. Please try again.');
                    } finally {
                      setRescheduling(false);
                    }
                  }}
                  disabled={rescheduling}
                  className="w-full px-4 py-3 rounded-lg bg-theme-muted dark:bg-white/5 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/10 hover:bg-theme-border dark:hover:bg-white/10 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reschedule Only This Workout
                </button>
                
                <button
                  onClick={async () => {
                    setRescheduling(true);
                    try {
                      const originalDate = new Date(sessions[0].scheduledDate);
                      const newDate = new Date(newScheduledDate);
                      const dateOffset = Math.round((newDate.getTime() - originalDate.getTime()) / (1000 * 60 * 60 * 24));

                      // Get all future sessions (including the current one)
                      const memberId = profile?.id || user?.id;
                      if (!memberId) {
                        throw new Error('Member ID not found');
                      }

                      // Get all sessions scheduled AFTER the current workout date
                      const futureSessions = await WorkoutService.getFutureWorkoutSessions(
                        memberId,
                        sessions[0].scheduledDate.split('T')[0]
                      );

                      // Include the current session + all future sessions
                      const sessionIds = [sessions[0].id, ...futureSessions.map(s => s.id)];
                      
                      await WorkoutService.updateWorkoutSessionDates(sessionIds, dateOffset);

                      // Refresh dashboard cache so closing the page shows fresh calendar without loading
                      await refreshDashboardCache(memberId);
                      
                      CacheService.invalidate(workoutDetailsCacheKey(param ?? '', memberId));
                      
                      setShowRescheduleConfirmation(false);
                      setNewScheduledDate('');
                      await refetch();
                    } catch (error) {
                      console.error('Error rescheduling workouts:', error);
                      alert('Failed to reschedule workouts. Please try again.');
                    } finally {
                      setRescheduling(false);
                    }
                  }}
                  disabled={rescheduling}
                  className="w-full px-4 py-3 rounded-lg bg-theme-muted dark:bg-white/5 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/10 hover:bg-theme-border dark:hover:bg-white/10 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Move All Future Workouts
                </button>
              </div>

              <button
                onClick={() => {
                  setShowRescheduleConfirmation(false);
                  setNewScheduledDate('');
                }}
                disabled={rescheduling}
                className="w-full mt-4 px-4 py-3 rounded-lg bg-theme-muted dark:bg-white/5 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/10 hover:bg-theme-border dark:hover:bg-white/10 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exercise detail: same bottom sheet pattern as Other Actions */}
      <AnimatePresence>
        {selectedExercise && (
          <motion.div
            key="exercise-detail-sheet-backdrop"
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-end justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setSelectedExercise(null)}
          >
            <motion.div
              className="bg-theme-bg dark:bg-theme-bg-dark rounded-t-2xl shadow-2xl w-full overflow-hidden touch-none max-h-[90vh] flex flex-col border-t border-theme-border/80 dark:border-white/10"
              onClick={(e) => e.stopPropagation()}
              initial={{ y: '100%' }}
              animate={{ y: exerciseSheetDragY }}
              exit={{ y: '100%' }}
              transition={exerciseSheetDragY > 0 ? { duration: 0 } : { type: 'spring', damping: 30, stiffness: 400 }}
            >
              <div
                {...exerciseSheetDragBind()}
                className="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
              >
                <div className="w-9 h-1 rounded-full bg-theme-text/20 dark:bg-theme-off-white/25" />
              </div>

              <div className="px-4 pt-5 pb-3 flex-shrink-0 border-b border-theme-border dark:border-white/10">
                <h3 className="text-lg font-semibold text-theme-text dark:text-theme-off-white leading-tight break-words">
                  {selectedExercise.name}
                </h3>
                {selectedExercise.muscleGroup && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="workout-details-muscle-tag">{selectedExercise.muscleGroup}</span>
                  </div>
                )}
              </div>

              <div
                className="overflow-y-auto flex-1 min-h-0 px-4 pt-4"
                style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
              >
                {selectedExercise.video && isYouTubeUrl(selectedExercise.video) && (
                  <div className="mb-5 w-full">
                    <div className="relative w-full rounded-xl overflow-hidden bg-black dark:bg-neutral-900" style={{ paddingBottom: '56.25%' }}>
                      {!exerciseVideoEmbedActive ? (
                        <button
                          type="button"
                          className="absolute inset-0 z-10 flex items-center justify-center group outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-theme-bg dark:focus-visible:ring-offset-theme-bg-dark"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExerciseVideoEmbedActive(true);
                          }}
                          aria-label={`Play video: ${selectedExercise.name}`}
                        >
                          {getYouTubeThumbnail(selectedExercise.video) ? (
                            <img
                              src={getYouTubeThumbnail(selectedExercise.video) || undefined}
                              alt=""
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          ) : null}
                          <span className="absolute inset-0 bg-black/25 group-hover:bg-black/35 transition-colors" aria-hidden />
                          <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-black/55 text-white shadow-lg ring-2 ring-white/80 group-hover:bg-black/70">
                            <Play className="h-7 w-7 ml-0.5" fill="currentColor" strokeWidth={0} aria-hidden />
                          </span>
                        </button>
                      ) : (
                        <iframe
                          src={getYouTubeEmbedUrl(selectedExercise.video, { autoplay: true }) || ''}
                          title={selectedExercise.name}
                          className="absolute top-0 left-0 h-full w-full rounded-xl border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                          allowFullScreen
                          referrerPolicy="strict-origin-when-cross-origin"
                        />
                      )}
                    </div>
                    {exerciseVideoEmbedActive && selectedExercise.video && isYouTubeUrl(selectedExercise.video) ? (
                      <p className="mt-1.5 text-center text-xs text-theme-text/55 dark:text-theme-off-white/55">
                        Plays muted so it starts in one tap — use the video controls for sound.
                      </p>
                    ) : null}
                  </div>
                )}

                {selectedExercise.description ? (
                  <div>
                    <h4 className="text-sm font-semibold text-theme-text/70 dark:text-theme-off-white/75 uppercase tracking-wide mb-2">
                      Description
                    </h4>
                    <p className="text-base leading-relaxed text-theme-text/85 dark:text-theme-off-white/85 whitespace-pre-wrap pb-2">
                      {selectedExercise.description}
                    </p>
                  </div>
                ) : !selectedExercise.video || !isYouTubeUrl(selectedExercise.video) ? (
                  <p className="text-sm text-theme-text/55 dark:text-theme-off-white/55 pb-2">No extra details for this exercise.</p>
                ) : null}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Action Bar - Fixed at bottom of screen */}
      {sessions.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-theme-bg dark:bg-theme-bg-dark border-t border-theme-border dark:border-white/10 z-50 p-4" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between gap-4">
              {/* Primary Action Button */}
              {sessions[0].status === 'completed' ? (
                <div className="flex-1 px-4 py-3 rounded-lg bg-primary-400 text-gray-800 dark:text-gray-900 flex items-center justify-center gap-2 font-medium">
                  <CheckCircle className="w-5 h-5 text-gray-800 dark:text-gray-900" />
                  <span>Completed</span>
                </div>
              ) : sessions[0].status === 'skipped' ? (
                <div className="flex-1 px-4 py-3 rounded-lg bg-gray-100 dark:bg-white/10 text-theme-text dark:text-theme-off-white border border-theme-border flex items-center justify-center gap-2 font-medium">
                  <Ban className="w-5 h-5" />
                  <span>Skipped</span>
                </div>
              ) : (sessions[0].status === 'scheduled' || sessions[0].status === 'in_progress') ? (
                <CompleteWorkoutSlider
                  onComplete={() => {
                    setSliderCompleting(true);
                    handleStatusChange(sessions[0].id, 'completed');
                  }}
                  disabled={updatingStatus === sessions[0].id}
                />
              ) : null}

              {/* Three Dots Menu */}
              <div className="relative">
                <button
                  onClick={() => {
                    if (openActionsMenuId === sessions[0].id) {
                      setOpenActionsMenuId(null);
                    } else {
                      setSelectedExercise(null);
                      setOpenActionsMenuId(sessions[0].id);
                    }
                  }}
                  disabled={updatingStatus === sessions[0].id && !sliderCompleting}
                  className="p-3 rounded-lg hover:bg-theme-muted dark:hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Other actions"
                >
                  <MoreVertical className="w-5 h-5 text-theme-text/70 dark:text-theme-off-white/70" />
                </button>

                {/* Other Actions Modal - iOS-style bottom sheet */}
                <AnimatePresence>
                {openActionsMenuId === sessions[0].id && (
                  <motion.div
                    key="sheet-backdrop"
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => setOpenActionsMenuId(null)}
                  >
                    <motion.div
                      className="bg-theme-bg dark:bg-theme-bg-dark rounded-t-2xl shadow-2xl w-full overflow-hidden touch-none"
                      onClick={(e) => e.stopPropagation()}
                      initial={{ y: '100%' }}
                      animate={{ y: sheetDragY }}
                      exit={{ y: '100%' }}
                      transition={sheetDragY > 0 ? { duration: 0 } : { type: 'spring', damping: 30, stiffness: 400 }}
                    >
                      {/* Drag handle */}
                      <div
                        {...sheetDragBind()}
                        className="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none"
                      >
                        <div className="w-9 h-1 rounded-full bg-theme-text/20 dark:bg-theme-off-white/25" />
                      </div>

                      <div className="px-4 pt-2" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}>
                        <h3 className="text-lg font-semibold text-theme-text dark:text-theme-off-white mb-3 text-center">Other Actions</h3>
                          
                        <div className="space-y-2">
                          {/* Revert to Scheduled - Show when completed or skipped */}
                          {(sessions[0].status === 'completed' || sessions[0].status === 'skipped') && (
                            <button
                              onClick={() => {
                                handleStatusChange(sessions[0].id, 'scheduled');
                                setOpenActionsMenuId(null);
                              }}
                              disabled={updatingStatus === sessions[0].id}
                              className="w-full px-4 py-3.5 rounded-xl bg-theme-muted dark:bg-white/5 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/10 hover:bg-theme-border dark:hover:bg-white/10 transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Calendar className="w-5 h-5" />
                              <span className="font-medium">Revert to Scheduled</span>
                            </button>
                          )}
                            
                          {/* Complete button - Show when skipped */}
                          {sessions[0].status === 'skipped' && (
                            <button
                              onClick={() => {
                                handleStatusChange(sessions[0].id, 'completed');
                                setOpenActionsMenuId(null);
                              }}
                              disabled={updatingStatus === sessions[0].id}
                              className="w-full px-4 py-3.5 rounded-xl bg-theme-muted dark:bg-white/5 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/10 hover:bg-theme-border dark:hover:bg-white/10 transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <CheckCircle className="w-5 h-5" />
                              <span className="font-medium">Complete</span>
                            </button>
                          )}

                          {/* Pause - Show when in_progress (reverts to scheduled) */}
                          {sessions[0].status === 'in_progress' && (
                            <button
                              onClick={() => {
                                handleStatusChange(sessions[0].id, 'scheduled');
                                setOpenActionsMenuId(null);
                              }}
                              disabled={updatingStatus === sessions[0].id}
                              className="w-full px-4 py-3.5 rounded-xl bg-theme-muted dark:bg-white/5 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/10 hover:bg-theme-border dark:hover:bg-white/10 transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Pause className="w-5 h-5" />
                              <span className="font-medium">Pause</span>
                            </button>
                          )}

                          {/* Cancel Workout - Show when in_progress (marks as skipped) */}
                          {sessions[0].status === 'in_progress' && (
                            <button
                              onClick={() => {
                                handleStatusChange(sessions[0].id, 'skipped');
                                setOpenActionsMenuId(null);
                              }}
                              disabled={updatingStatus === sessions[0].id}
                              className="w-full px-4 py-3.5 rounded-xl bg-theme-muted dark:bg-white/5 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/10 hover:bg-theme-border dark:hover:bg-white/10 transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <XCircle className="w-5 h-5" />
                              <span className="font-medium">Cancel Workout</span>
                            </button>
                          )}
                            
                          {/* Skip button - only show if status is scheduled */}
                          {sessions[0].status === 'scheduled' && (
                            <button
                              onClick={() => {
                                handleStatusChange(sessions[0].id, 'skipped');
                                setOpenActionsMenuId(null);
                              }}
                              disabled={updatingStatus === sessions[0].id}
                              className="w-full px-4 py-3.5 rounded-xl bg-theme-muted dark:bg-white/5 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/10 hover:bg-theme-border dark:hover:bg-white/10 transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <XCircle className="w-5 h-5" />
                              <span className="font-medium">Skip</span>
                            </button>
                          )}
                            
                          {/* Reschedule button - Show when scheduled, in_progress, or skipped */}
                          {(sessions[0].status === 'scheduled' || sessions[0].status === 'in_progress' || sessions[0].status === 'skipped') && (
                            <button
                              onClick={() => {
                                setOpenActionsMenuId(null);
                                const currentDate = sessions[0].scheduledDate.split('T')[0];
                                setNewScheduledDate(currentDate);
                                setShowRescheduleModal(true);
                              }}
                              disabled={updatingStatus === sessions[0].id}
                              className="w-full px-4 py-3.5 rounded-xl bg-theme-muted dark:bg-white/5 text-theme-text dark:text-theme-off-white border border-theme-border dark:border-white/10 hover:bg-theme-border dark:hover:bg-white/10 transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Calendar className="w-5 h-5" />
                              <span className="font-medium">Reschedule</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

